import { useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import moment, { Moment } from "moment"
import { Plus } from "~/library/UIkit/Icons"

import PageContainer from "~/library/PageContainer"
import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"
import DateNavigation from "~/library/UIkit/MonthNavigation"
import ExpandableSearch from "~/library/UIkit/ExpandableSearch"
import EventCalendar from "~/library/UIkit/EventCalendar"
import AccordionSection from "~/library/UIkit/AccordionSection"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { saveNavigationState } from "~/utils/navigationState"
import { apiSponsorshipAreas, apiSponsorships } from "~/routes"
import Sponsorship from "~/types/Sponsorship"
import type { Area, AreaType } from "~/types/sponsorship/Area"

import NewArea from "~/views/Sponsorship/NewArea"
import EditArea from "~/views/Sponsorship/EditArea"
import NewSponsorship from "~/views/Sponsorship/NewSponsorship"

type CalendarView = "month" | "week" | "day"

const getDateRangeForView = (view: CalendarView): [Moment, Moment] => {
  switch (view) {
    case "month":
      return [moment().startOf("month"), moment().endOf("month")]
    case "week":
      return [moment().startOf("week"), moment().endOf("week")]
    case "day":
      return [moment().startOf("day"), moment().endOf("day")]
    default:
      return [moment().startOf("month"), moment().endOf("month")]
  }
}

const transformSponsorshipsToEntries = (sponsorships: Sponsorship[]) => {
  return sponsorships.map((sponsorship) => ({
    id: sponsorship.id,
    title: sponsorship.company.name,
    sponsor: sponsorship.company.contactPerson,
    startDate: sponsorship.startOn,
    endDate: sponsorship.endOn,
    areaId: sponsorship.area.id,
  }))
}

const SponsorshipsList = () => {
  const { venueSlug } = getAppConfig()
  const navigate = useNavigate()
  const location = useLocation()
  const [calendarView, setCalendarView] = useState<CalendarView>("month")
  const [searchQuery, setSearchQuery] = useState("")
  const [physicalExpanded, setPhysicalExpanded] = useState(true)
  const [digitalExpanded, setDigitalExpanded] = useState(true)

  const [startDate, setStartDate] = useState<Moment>(moment().startOf("month"))
  const [endDate, setEndDate] = useState<Moment>(moment().endOf("month"))

  // Modal state
  const [isNewAreaModalOpen, setIsNewAreaModalOpen] = useState(false)
  const [isEditAreaModalOpen, setIsEditAreaModalOpen] = useState(false)
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null)
  const [currentSectionType, setCurrentSectionType] = useState<AreaType | null>(null)
  const [isNewSponsorshipModalOpen, setIsNewSponsorshipModalOpen] = useState(false)

  const [selectedAreaInfo, setSelectedAreaInfo] = useState<{
    areaType: string
    areaId?: string
    cellDate?: string
  } | null>(null)

  const sortingParams = { "q[s]": "created_at asc" }

  const dateRangeParams = {
    "q[start_on_lteq]": endDate.format("YYYY-MM-DD"),
    "q[end_on_gteq]": startDate.format("YYYY-MM-DD"),
  }

  const {
    data: physicalAreas,
    isLoading: isLoadingPhysical,
    refetch: refetchPhysical,
  } = useFetchResource<{ sponsorshipAreas: Area[] }, "sponsorshipAreas">(
    apiSponsorshipAreas.index.path({ venueSlug }),
    "sponsorshipAreas",
    {
      "q[area_type_eq]": "physical",
      ...sortingParams,
    }
  )

  const {
    data: digitalAreas,
    isLoading: isLoadingDigital,
    refetch: refetchDigital,
  } = useFetchResource<{ sponsorshipAreas: Area[] }, "sponsorshipAreas">(
    apiSponsorshipAreas.index.path({ venueSlug }),
    "sponsorshipAreas",
    {
      "q[area_type_eq]": "digital",
      ...sortingParams,
    }
  )

  const {
    data: physicalSponsorships,
    isRefetching: isLoadingPhysicalSponsorships,
    refetch: refetchPhysicalSponsorships,
  } = useFetchResource<{ sponsorships: Sponsorship[] }, "sponsorships">(
    apiSponsorships.index.path({ venueSlug }),
    "sponsorships",
    {
      "q[area_type_eq]": "physical",
      ...dateRangeParams,
      ...sortingParams,
    }
  )

  const {
    data: digitalSponsorships,
    isRefetching: isLoadingDigitalSponsorships,
    refetch: refetchDigitalSponsorships,
  } = useFetchResource<{ sponsorships: Sponsorship[] }, "sponsorships">(
    apiSponsorships.index.path({ venueSlug }),
    "sponsorships",
    {
      "q[area_type_eq]": "digital",
      ...dateRangeParams,
      ...sortingParams,
    }
  )

  const handleSponsorshipsRefetch = (from: Moment, to: Moment) => {
    const newDateRangeParams = {
      "q[start_on_lteq]": to.format("YYYY-MM-DD"),
      "q[end_on_gteq]": from.format("YYYY-MM-DD"),
    }

    refetchPhysicalSponsorships({
      "q[area_type_eq]": "physical",
      ...newDateRangeParams,
      ...sortingParams,
    })

    refetchDigitalSponsorships({
      "q[area_type_eq]": "digital",
      ...newDateRangeParams,
      ...sortingParams,
    })
  }

  const handleDateRangeChange = (newStartDate: Moment, newEndDate: Moment) => {
    setStartDate(newStartDate)
    setEndDate(newEndDate)

    handleSponsorshipsRefetch(newStartDate, newEndDate)
  }

  const handleCalendarViewChange = (view: CalendarView) => {
    setCalendarView(view)
    const [newStart, newEnd] = getDateRangeForView(view)
    handleDateRangeChange(newStart, newEnd)
  }

  const handleNewPhysicalArea = () => {
    setCurrentSectionType("physical")
    setIsNewAreaModalOpen(true)
  }

  const handleNewDigitalArea = () => {
    setCurrentSectionType("digital")
    setIsNewAreaModalOpen(true)
  }

  const handlePhysicalAreaClick = (area: Area) => {
    setCurrentSectionType("physical")
    setSelectedAreaId(area.id)
    setIsEditAreaModalOpen(true)
  }

  const handleDigitalAreaClick = (area: Area) => {
    setCurrentSectionType("digital")
    setSelectedAreaId(area.id)
    setIsEditAreaModalOpen(true)
  }

  const handleCellClick = (area: Area, entryId?: string, date?: Moment) => {
    const areaInfo = {
      areaType: area.areaType,
      areaId: area.uuid,
      cellDate: date ? date.format("YYYY-MM-DD") : undefined,
    }

    setSelectedAreaInfo(areaInfo)

    if (entryId) {
      saveNavigationState("sponsorship", location.pathname, "Sponsorship")
      navigate(`/sponsorship/${entryId}`)
    } else {
      setIsNewSponsorshipModalOpen(true)
    }
  }

  // Modal handlers
  const handleCloseNewAreaModal = () => {
    setIsNewAreaModalOpen(false)
    setCurrentSectionType(null)
  }

  const handleCloseEditAreaModal = () => {
    setIsEditAreaModalOpen(false)
    setSelectedAreaId(null)
    setCurrentSectionType(null)
  }

  const handleAreaSuccess = (areaType: AreaType) => {
    if (areaType === "physical") {
      refetchPhysical({
        "q[area_type_eq]": "physical",
        ...sortingParams,
      })
    } else if (areaType === "digital") {
      refetchDigital({
        "q[area_type_eq]": "digital",
        ...sortingParams,
      })
    }
  }

  // Sponsorship modal handlers
  const handleCloseSponsorshipModals = () => {
    setIsNewSponsorshipModalOpen(false)
    setSelectedAreaInfo(null)
  }

  const handleSponsorshipSuccess = (areaType: AreaType) => {
    if (areaType === "physical") {
      refetchPhysicalSponsorships({
        "q[area_type_eq]": "physical",
        ...dateRangeParams,
        ...sortingParams,
      })
    } else if (areaType === "digital") {
      refetchDigitalSponsorships({
        "q[area_type_eq]": "digital",
        ...dateRangeParams,
        ...sortingParams,
      })
    }
  }

  const SponsorshipPageControls = () => (
    <div className="sponsorship-controls">
      <DateNavigation
        startDate={startDate}
        endDate={endDate}
        calendarView={calendarView}
        onDateRangeChange={handleDateRangeChange}
        onCalendarViewChange={handleCalendarViewChange}
      />
      <ExpandableSearch
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search campaigns..."
      />
      <button
        className="btn-catch-primary btn btn-primary"
        onClick={() => {
          setSelectedAreaInfo(null) // No pre-selected area from button
          setIsNewSponsorshipModalOpen(true)
        }}
      >
        <Plus color={"#ffffff"} />
      </button>
    </div>
  )

  return (
    <PageContainer>
      <PageTitle title="Sponsorship">
        <SponsorshipPageControls />
      </PageTitle>

      <div className="row">
        <div className="col-12">
          <CatchCard style={{ backgroundColor: "transparent" }}>
            <AccordionSection
              title="Physical Sponsorship Areas"
              arrowBeforeTitle={true}
              isExpanded={physicalExpanded}
              onToggle={() => setPhysicalExpanded((prev) => !prev)}
            >
              <LoadingWrapper isLoading={isLoadingPhysical}>
                <EventCalendar
                  view={calendarView}
                  startDate={startDate}
                  endDate={endDate}
                  areas={physicalAreas}
                  entries={transformSponsorshipsToEntries(physicalSponsorships || [])}
                  isLoading={isLoadingPhysicalSponsorships}
                  onNewArea={handleNewPhysicalArea}
                  onAreaClick={handlePhysicalAreaClick}
                  onCellClick={handleCellClick}
                />
              </LoadingWrapper>
            </AccordionSection>
            <div className="mt-2" />
            <AccordionSection
              title="Digital / In-Venue Media Sponsorships"
              arrowBeforeTitle={true}
              isExpanded={digitalExpanded}
              onToggle={() => setDigitalExpanded((prev) => !prev)}
            >
              <LoadingWrapper isLoading={isLoadingDigital}>
                <EventCalendar
                  view={calendarView}
                  startDate={startDate}
                  endDate={endDate}
                  areas={digitalAreas}
                  entries={transformSponsorshipsToEntries(digitalSponsorships || [])}
                  isLoading={isLoadingDigitalSponsorships}
                  onNewArea={handleNewDigitalArea}
                  onAreaClick={handleDigitalAreaClick}
                  onCellClick={handleCellClick}
                />
              </LoadingWrapper>
            </AccordionSection>
          </CatchCard>
        </div>
      </div>

      {isNewAreaModalOpen && currentSectionType && (
        <NewArea
          isOpen={isNewAreaModalOpen}
          onClose={handleCloseNewAreaModal}
          onSuccess={handleAreaSuccess}
          areaType={currentSectionType}
        />
      )}

      {isEditAreaModalOpen && currentSectionType && selectedAreaId && (
        <EditArea
          isOpen={isEditAreaModalOpen}
          onClose={handleCloseEditAreaModal}
          areaId={selectedAreaId}
          onSuccess={handleAreaSuccess}
          areaType={currentSectionType}
        />
      )}

      {isNewSponsorshipModalOpen && (
        <NewSponsorship
          isOpen={isNewSponsorshipModalOpen}
          onClose={handleCloseSponsorshipModals}
          onSuccess={handleSponsorshipSuccess}
          areaInfo={selectedAreaInfo}
        />
      )}
    </PageContainer>
  )
}

export default SponsorshipsList
